// Custom Hook for F1 Driver Data
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useF1Championship } from './useF1Championship';
import { useGrillTheGrid } from './useGrillTheGrid';

/**
 * Custom hook for loading specific F1 driver data
 * @param {string|number} driverId - ID of the driver to load
 * @returns {Object} - Driver data, loading state, error state, and utility functions
 */
export const useF1Driver = (driverId) => {
  const { data, loading, error } = useF1Championship();
  const { getDataByDriver: getGrillTheGridData } = useGrillTheGrid();
  const [driverData, setDriverData] = useState(null);
  const [driverLoading, setDriverLoading] = useState(true);
  const [driverError, setDriverError] = useState(null);

  // Process driver data when main data loads
  useEffect(() => {
    if (loading) {
      setDriverLoading(true);
      setDriverError(null);
      return;
    }

    if (error) {
      setDriverError(error);
      setDriverLoading(false);
      return;
    }

    if (!data || !driverId) {
      setDriverError('No data available');
      setDriverLoading(false);
      return;
    }

    try {
      // Find the specific driver by driverId
      const driver = data.drivers?.find(d => d.driverId == driverId);
      
      if (!driver) {
        setDriverError(`Driver with ID ${driverId} not found`);
        setDriverLoading(false);
        return;
      }

      // Get season standings for this driver from driver_standings
      // Group by year and take the final standing (last race of each year)
      const standingsByYear = {};
      
      data.driver_standings?.forEach(standing => {
        if (standing.driverId != driverId) return;
        
        const race = data.races?.find(r => r.raceId == standing.raceId);
        if (!race) return;
        
        const year = race.year;
        // Keep the entry with the highest raceId (last race of the year)
        if (!standingsByYear[year] || standing.raceId > standingsByYear[year].raceId) {
          standingsByYear[year] = {
            year: parseInt(year),
            points: parseFloat(standing.points) || 0,
            position: parseInt(standing.position),
            wins: parseInt(standing.wins) || 0,
            raceId: standing.raceId
          };
        }
      });

      const seasonStandings = Object.values(standingsByYear).sort((a, b) => b.year - a.year);
      
      // Calculate driver statistics from season standings
      const totalPoints = seasonStandings.reduce((sum, standing) => sum + standing.points, 0);
      const totalWins = seasonStandings.reduce((sum, standing) => sum + standing.wins, 0);
      const bestPosition = seasonStandings.length > 0 
        ? Math.min(...seasonStandings.map(s => s.position).filter(p => p > 0))
        : null;
      const careerChampionships = seasonStandings.filter(s => s.position === 1).length;
      
      // Get races where driver participated using results.csv
      const driverRaceIds = new Set();
      const resultsByRace = {};
      data.results?.forEach(result => {
        if (result.driverId == driverId) {
          driverRaceIds.add(result.raceId);
          resultsByRace[result.raceId] = {
            position: result.position ? parseInt(result.position) : null,
            positionText: result.positionText,
            points: parseFloat(result.points) || 0,
            laps: parseInt(result.laps) || 0
          };
        }
      });

      // Get qualifying results for driver's races
      const qualifyingByRace = {};
      data.qualifying?.forEach(qual => {
        if (qual.driverId == driverId) {
          qualifyingByRace[qual.raceId] = {
            position: qual.position ? parseInt(qual.position) : null,
            q1: qual.q1,
            q2: qual.q2,
            q3: qual.q3
          };
        }
      });

      // Get race details for races driver participated in
      const driverRaces = data.races
        ?.filter(race => driverRaceIds.has(race.raceId))
        .map(race => ({
          raceId: race.raceId,
          year: parseInt(race.year),
          round: parseInt(race.round),
          name: race.name,
          circuit: race.circuit,
          date: race.date,
          qualifying: qualifyingByRace[race.raceId] || null,
          result: resultsByRace[race.raceId] || null
        }))
        .sort((a, b) => b.year - a.year || b.round - a.round) || [];

      // Calculate career statistics from race results
      const qualifyingPositions = driverRaces
        .map(race => race.qualifying?.position)
        .filter(pos => pos != null && pos > 0);
      
      const resultPositions = driverRaces
        .map(race => race.result?.position)
        .filter(pos => pos != null && pos > 0);

      const careerQualifyingAverage = qualifyingPositions.length > 0
        ? (qualifyingPositions.reduce((sum, pos) => sum + pos, 0) / qualifyingPositions.length).toFixed(2)
        : 'N/A';
      
      const careerResultAverage = resultPositions.length > 0
        ? (resultPositions.reduce((sum, pos) => sum + pos, 0) / resultPositions.length).toFixed(2)
        : 'N/A';
      
      const careerBestQualifying = qualifyingPositions.length > 0
        ? Math.min(...qualifyingPositions)
        : 'N/A';
      
      const careerBestResult = resultPositions.length > 0
        ? Math.min(...resultPositions)
        : 'N/A';
      
      const careerWorstQualifying = qualifyingPositions.length > 0
        ? Math.max(...qualifyingPositions)
        : 'N/A';
      
      const careerWorstResult = resultPositions.length > 0
        ? Math.max(...resultPositions)
        : 'N/A';
      
      const careerPoles = qualifyingPositions.filter(pos => pos === 1).length;
      const careerPodiums = resultPositions.filter(pos => pos <= 3).length;

      // Calculate best circuit by average finishing position
      const circuitResults = {};
      driverRaces.forEach(race => {
        if (race.result?.position && race.result.position > 0) {
          const circuit = race.circuit;
          if (!circuitResults[circuit]) {
            circuitResults[circuit] = { total: 0, count: 0 };
          }
          circuitResults[circuit].total += race.result.position;
          circuitResults[circuit].count += 1;
        }
      });

      let bestCircuit = 'N/A';
      let bestCircuitAvg = Infinity;
      Object.entries(circuitResults).forEach(([circuit, data]) => {
        const avg = data.total / data.count;
        if (avg < bestCircuitAvg && data.count >= 2) {
          bestCircuitAvg = avg;
          bestCircuit = circuit;
        }
      });

      // Create circuit averages array for table display
      const circuitAverages = Object.entries(circuitResults)
        .map(([circuit, data]) => ({
          circuit,
          average: (data.total / data.count).toFixed(2),
          races: data.count
        }))
        .sort((a, b) => parseFloat(a.average) - parseFloat(b.average));

      // Get Grill The Grid data for this driver
      const grillTheGridData = getGrillTheGridData(driverId);

      // Create enhanced driver object
      const enhancedDriver = {
        ...driver,
        fullName: `${driver.forename} ${driver.surname}`,
        totalPoints,
        totalWins,
        bestPosition,
        seasonsParticipated: seasonStandings.length,
        standings: seasonStandings,
        races: driverRaces,
        careerQualifyingAverage,
        careerResultAverage,
        careerBestQualifying,
        careerBestResult,
        careerWorstQualifying,
        careerWorstResult,
        careerPoles,
        careerPodiums,
        careerChampionships,
        bestCircuit,
        bestCircuitAvg,
        circuitAverages,
        grillTheGrid: grillTheGridData
      };

      setDriverData(enhancedDriver);
      setDriverError(null);
    } catch (err) {
      setDriverError(err.message || 'Failed to process driver data');
    } finally {
      setDriverLoading(false);
    }
  }, [data, loading, error, driverId, getGrillTheGridData]);

  // Get driver by name (utility function)
  const getDriverByName = useCallback((name) => {
    if (!data?.drivers) return null;
    return data.drivers.find(driver => 
      `${driver.forename} ${driver.surname}`.toLowerCase() === name.toLowerCase()
    );
  }, [data]);

  // Get top drivers (utility function)
  const getTopDrivers = useCallback((limit = 10) => {
    if (!data?.drivers) return [];
    
    // Calculate points for each driver using driver_standings
    const driversWithStats = data.drivers.map(driver => {
      // Group standings by year and take final standing
      const standingsByYear = {};
      data.driver_standings?.forEach(standing => {
        if (standing.driverId != driver.driverId) return;
        
        const race = data.races?.find(r => r.raceId == standing.raceId);
        if (!race) return;
        
        const year = race.year;
        if (!standingsByYear[year] || standing.raceId > standingsByYear[year].raceId) {
          standingsByYear[year] = {
            points: parseFloat(standing.points) || 0,
            wins: parseInt(standing.wins) || 0,
            raceId: standing.raceId
          };
        }
      });

      const yearStandings = Object.values(standingsByYear);
      const totalPoints = yearStandings.reduce((sum, s) => sum + s.points, 0);
      const totalWins = yearStandings.reduce((sum, s) => sum + s.wins, 0);
      
      return {
        ...driver,
        fullName: `${driver.forename} ${driver.surname}`,
        totalPoints,
        totalWins
      };
    });

    return driversWithStats
      .sort((a, b) => b.totalPoints - a.totalPoints)
      .slice(0, limit);
  }, [data]);

  // Memoized values
  const memoizedData = useMemo(() => driverData, [driverData]);

  return {
    driver: memoizedData,
    loading: driverLoading,
    error: driverError,
    getDriverByName,
    getTopDrivers
  };
};
